class SwiperPagination extends HTMLElement {
  connectedCallback() {
    this.swiperSelector = this.getAttribute("swiper-selector");
    this.paginationType = this.getAttribute("type") || "bullets";
    if (!this.swiperSelector) {
      console.error("swiper-selector attribute is required");
      return;
    }
    this.init();
  }
  init() {
    this.waitForSwiper().then(() => {
      this.setupPagination();
    });
  }
  waitForSwiper() {
    return new Promise((resolve) => {
      if (customElements.get("swiper-container")) {
        resolve();
      } else {
        const checkInterval = setInterval(() => {
          if (customElements.get("swiper-container")) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      }
    });
  }
  setupPagination() {
    const swiperEl = document.querySelector(this.swiperSelector);
    if (!swiperEl) return;
    const initSwiper = () => {
      if (swiperEl.swiper) {
        this.swiper = swiperEl.swiper;
        this.render();
        this.attachEventListeners();
        this.disableHoverDuringSwipe(swiperEl);
        setTimeout(() => {
          this.updatePagination();
        }, 50);
      } else {
        setTimeout(initSwiper, 100);
      }
    };
    initSwiper();
  }
  disableHoverDuringSwipe(swiperEl) {
    this.swiper.on("sliderFirstMove", () => {
      swiperEl.classList.add("is-dragging");
    });
    this.swiper.on("touchEnd", () => {
      swiperEl.classList.remove("is-dragging");
    });
  }
  getRealSlideCount() {
    if (!this.swiper.params.loop) return this.swiper.slides.length;
    const indices = new Set(
      this.swiper.slides.map((s) => s.getAttribute("data-swiper-slide-index"))
    );
    return indices.size;
  }
  render() {
    const totalSlides = this.getRealSlideCount();
    if (this.paginationType === "bullets") {
      this.renderBullets(totalSlides);
    } else if (this.paginationType === "numbers") {
      this.renderNumbers(totalSlides);
    } else if (this.paginationType === "fraction") {
      this.renderFraction(totalSlides);
    } else if (this.paginationType === "navigation") {
      this.renderNavigation(totalSlides);
    } else if (this.paginationType === "arrows") {
      this.renderArrows();
    }
  }
  renderBullets(totalSlides) {
    this.innerHTML = `
      <div class="flex items-center justify-center gap-2">
        ${Array.from(
      { length: totalSlides },
      (_, i) => `
          <button
            class="pagination-bullet h-2 w-2 rounded-full transition-all duration-300 ${i === 0 ? "bg-black w-8" : "bg-gray-300"}"
            data-index="${i}"
            aria-label="Go to slide ${i + 1}"
          ></button>
        `
    ).join("")}
      </div>
    `;
  }
  renderNumbers(totalSlides) {
    this.innerHTML = `
      <div class="flex items-center justify-center gap-2">
        ${Array.from(
      { length: totalSlides },
      (_, i) => `
          <button
            class="pagination-number flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 ${i === 0 ? "bg-black text-white" : "bg-gray-200 text-gray-700"}"
            data-index="${i}"
            aria-label="Go to slide ${i + 1}"
          >${i + 1}</button>
        `
    ).join("")}
      </div>
    `;
  }
  renderFraction(totalSlides) {
    this.innerHTML = `
      <div class="flex items-center justify-center gap-2 text-sm">
        <span class="current-slide font-semibold">1</span>
        <span class="text-gray-500">/</span>
        <span class="total-slides text-gray-500">${totalSlides}</span>
      </div>
    `;
  }
  renderNavigation(totalSlides) {
    this.innerHTML = `
      <div class="flex items-center justify-center gap-6">
        <button
          class="nav-prev cursor-pointer flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 transition-all duration-300 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-30" style="touch-action:manipulation"
          aria-label="Previous slide"
        >
					<svg
						class="h-4 w-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
					</svg>
        </button>
        <div class="flex items-center gap-2 text-sm">
          <span class="current-slide font-semibold">1</span>
          <span class="text-gray-500">/</span>
          <span class="total-slides text-gray-500">${totalSlides}</span>
        </div>
        <button
          class="nav-next cursor-pointer flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 transition-all duration-300 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-30" style="touch-action:manipulation"
          aria-label="Next slide"
        >
					<svg
						class="h-4 w-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
					</svg>
        </button>
      </div>
    `;
  }
  renderArrows() {
    this.innerHTML = `
      <div class="pointer-events-none flex w-full items-center justify-between">
        <button
          class="pointer-events-auto nav-prev group flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white transition-all duration-300 hover:bg-black/80 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-black/50 disabled:hover:scale-100" style="touch-action:manipulation"
          aria-label="Previous slide"
        >
          <div class="relative h-6 w-6">
            <svg
              class="absolute inset-0 h-6 w-6 transition-all duration-300 group-hover:-translate-x-2 group-hover:opacity-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            <svg
              class="absolute inset-0 h-6 w-6 translate-x-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
          </div>
        </button>
        <button
          class="pointer-events-auto nav-next group flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white transition-all duration-300 hover:bg-black/80 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-black/50 disabled:hover:scale-100" style="touch-action:manipulation"
          aria-label="Next slide"
        >
          <div class="relative h-6 w-6">
            <svg
              class="absolute inset-0 h-6 w-6 transition-all duration-300 group-hover:translate-x-2 group-hover:opacity-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
            <svg
              class="absolute inset-0 h-6 w-6 -translate-x-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
            </svg>
          </div>
        </button>
      </div>
    `;
  }
  attachEventListeners() {
    this.swiper.on("slideChange", () => {
      this.updatePagination();
    });
    if (this.paginationType === "bullets" || this.paginationType === "numbers") {
      const buttons = this.querySelectorAll("[data-index]");
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          const index = parseInt(button.getAttribute("data-index"));
          this.swiper.slideTo(index);
        });
      });
    }
    if (this.paginationType === "navigation" || this.paginationType === "arrows") {
      const prevBtn = this.querySelector(".nav-prev");
      const nextBtn = this.querySelector(".nav-next");
      if (prevBtn) {
        prevBtn.addEventListener("click", () => {
          this.swiper.slidePrev();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener("click", () => {
          this.swiper.slideNext();
        });
      }
      this.updateNavigationButtons();
    }
  }
  updatePagination() {
    const isLoop = this.swiper.params.loop;
    const activeIndex = isLoop ? this.swiper.realIndex : this.swiper.activeIndex;
    const slidesPerView = this.swiper.params.slidesPerView;
    const totalSlides = this.getRealSlideCount();
    let lastVisibleSlide;
    if (typeof slidesPerView === "number") {
      lastVisibleSlide = Math.min(
        activeIndex + Math.round(slidesPerView) - 1,
        totalSlides - 1
      );
    } else {
      lastVisibleSlide = activeIndex;
    }
    const lastVisibleSlideNumber = lastVisibleSlide + 1;
    if (this.paginationType === "bullets") {
      const bullets = this.querySelectorAll(".pagination-bullet");
      bullets.forEach((bullet, index) => {
        if (index === lastVisibleSlide) {
          bullet.classList.remove("bg-gray-300", "w-2");
          bullet.classList.add("bg-black", "w-8");
        } else {
          bullet.classList.remove("bg-black", "w-8");
          bullet.classList.add("bg-gray-300", "w-2");
        }
      });
    } else if (this.paginationType === "numbers") {
      const numbers = this.querySelectorAll(".pagination-number");
      numbers.forEach((number, index) => {
        if (index === lastVisibleSlide) {
          number.classList.remove("bg-gray-200", "text-gray-700");
          number.classList.add("bg-black", "text-white");
        } else {
          number.classList.remove("bg-black", "text-white");
          number.classList.add("bg-gray-200", "text-gray-700");
        }
      });
    } else if (this.paginationType === "fraction" || this.paginationType === "navigation") {
      const currentSlide = this.querySelector(".current-slide");
      if (currentSlide) {
        currentSlide.textContent = lastVisibleSlideNumber;
      }
      if (this.paginationType === "navigation") {
        this.updateNavigationButtons();
      }
    } else if (this.paginationType === "arrows") {
      this.updateNavigationButtons();
    }
  }
  updateNavigationButtons() {
    const prevBtn = this.querySelector(".nav-prev");
    const nextBtn = this.querySelector(".nav-next");
    if (this.swiper.params.loop) {
      if (prevBtn) prevBtn.disabled = false;
      if (nextBtn) nextBtn.disabled = false;
      return;
    }
    if (prevBtn) {
      prevBtn.disabled = this.swiper.isBeginning;
    }
    if (nextBtn) {
      nextBtn.disabled = this.swiper.isEnd;
    }
  }
}
customElements.define("swiper-pagination", SwiperPagination);
